import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaSearch,
  FaQuestionCircle,
  FaLifeRing,
  FaClipboardList,
  FaUserShield,
  FaTools,
  FaArrowRight,
  FaChevronDown,
  FaTimes,
  FaExternalLinkAlt,
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// --- Content data -----------------------------------------------------------
const KB_ARTICLES = [
  {
    id: "medicare-101",
    title: "Medicare 101: Getting Started",
    blurb:
      "Understand Parts A, B, C, and D and learn the simple steps to enroll.",
    to: "/blog/medicare-101-getting-started",
    tags: ["medicare", "basics"],
  },
  {
    id: "medicare-advantage",
    title: "Choosing a Medicare Advantage Plan",
    blurb:
      "Compare Part C plan types, networks, and extras so you can decide with confidence.",
    to: "/medicare-a",
    tags: ["medicare", "advantage"],
  },
  {
    id: "medigap",
    title: "Medicare Supplement (Medigap) Basics",
    blurb:
      "What Medigap covers, how pricing works, and when to enroll.",
    to: "/medicare-supplements",
    tags: ["medicare", "supplement"],
  },
  {
    id: "part-d",
    title: "Part D Prescription Coverage",
    blurb: "Finding a drug plan that fits your prescriptions and budget.",
    to: "/medicare-d",
    tags: ["medicare", "rx"],
  },
  {
    id: "term-life",
    title: "Term Life Insurance: How It Works",
    blurb:
      "Coverage length, riders, underwriting, and how to compare quotes.",
    to: "/term-life",
    tags: ["life", "term"],
  },
  {
    id: "perm-life",
    title: "Permanent Life Insurance Guide",
    blurb:
      "Whole vs. universal life, cash value basics, and common use cases.",
    to: "/permanent-life",
    tags: ["life", "permanent"],
  },
  {
    id: "find-a-broker",
    title: "Find a Local Broker",
    blurb:
      "Use your ZIP code to browse licensed brokers near you.",
    to: "/plans",
    tags: ["brokers", "support"],
  },
  {
    id: "partner-apply",
    title: "Become a Partner (Agents)",
    blurb:
      "Submit your application with license & E&O and join our network.",
    to: "/partner-apply",
    tags: ["brokers", "agents"],
  },
  {
    id: "contact",
    title: "Contact Support",
    blurb:
      "Email our team or submit a request from this page.",
    to: "/contact",
    tags: ["support"],
  },
];

const FAQ = [
  {
    q: "How do I get matched with a broker?",
    a: (
      <>
        Go to <Link className="link" to="/plans">/plans</Link> and enter your ZIP code.
        Choose a broker to message, or submit the form and we'll alert the broker you pick.
      </>
    ),
  },
  {
    q: "What documents do I need for the Partner Application?",
    a: (
      <>
        You'll typically upload your <strong>state insurance license</strong> and <strong>E&O certificate</strong>.
        The form accepts PDF/JPG/PNG files. If you hit an error, check that your files are under 10MB and your internet connection is stable.
        You can apply at <Link className="link" to="/partner-apply">/partner-apply</Link>.
      </>
    ),
  },
  {
    q: "How quickly will a broker respond to my request?",
    a: (
      <>
        We notify brokers by email (and SMS if enabled). Responses usually arrive
        within <strong>1 business day</strong>. If it’s urgent, use the support form below and mention the deadline.
      </>
    ),
  },
  {
    q: "Can you help me compare Medicare Advantage vs. Medigap?",
    a: (
      <>
        Yes. Start with these guides: <Link className="link" to="/medicare-a">Medicare Advantage</Link> and
        <span> </span>
        <Link className="link" to="/medicare-supplements">Medicare Supplement</Link>. Then a broker can run side‑by‑side quotes for you.
      </>
    ),
  },
  {
    q: "I submitted the partner form but didn't get a confirmation.",
    a: (
      <>
        Check your spam folder and verify your email address was typed correctly. If still nothing after 15 minutes,
        contact support using the request form below and include your full name and NPN.
      </>
    ),
  },
  {
    q: "Is Your Choice Associates an insurance carrier?",
    a: (
      <>
        We’re an <strong>independent insurance agency</strong> that helps you compare plans across many carriers.
      </>
    ),
  },
];

// --- Helpers ----------------------------------------------------------------
const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.5, delay: d },
});

function normalize(str = "") {
  return str.toLowerCase().replace(/[^a-z0-9\s]/g, "").trim();
}

// --- Page -------------------------------------------------------------------
export default function HelpCenter() {
  const [q, setQ] = useState("");
  const [openIdx, setOpenIdx] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "General Question",
    message: "",
    consent: false,
  });

  const results = useMemo(() => {
    if (!q) return { faq: [], kb: [] };
    const n = normalize(q);
    const faq = FAQ.filter(
      (f) => normalize(f.q).includes(n) || normalize(String(f.a)).includes(n)
    );
    const kb = KB_ARTICLES.filter(
      (a) => normalize(a.title).includes(n) || normalize(a.blurb).includes(n)
    );
    return { faq, kb };
  }, [q]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSent(false);

    if (!form.name || !form.email || !form.message) {
      setErr("Please complete name, email, and your message.");
      return;
    }

    try {
      setSending(true);
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          subject: `Help Center: ${form.topic}`,
          message: form.message,
          company: "", // honeypot
          consent: form.consent,
          source: "help-center",
        }),
      });
      const data = await res.json().catch(() => ({ ok: false }));
      if (!res.ok || data?.ok === false) throw new Error(data?.error || "Failed to send");
      setSent(true);
      setForm({ name: "", email: "", phone: "", topic: "General Question", message: "", consent: false });
    } catch (e) {
      setErr(e?.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const CategoryCard = ({ icon: Icon, title, blurb, to }) => (
    <motion.div {...fadeUp(0.05)} className="group">
      <Link
        to={to}
        className="block h-full rounded-2xl border border-gray-200/60 bg-white p-5 shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#0B5EFD]/30"
      >
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-gray-100 p-3">
            <Icon className="text-xl" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-base font-semibold">{title}</h3>
            <p className="mt-1 text-sm text-gray-600">{blurb}</p>
            <span className="mt-3 inline-flex items-center text-sm font-medium text-[#0B5EFD]">
              Read more <FaArrowRight className="ml-2" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );

  const KBCard = ({ item }) => (
    <Link
      to={item.to}
      className="block rounded-2xl border border-gray-200/60 bg-white p-5 shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#0B5EFD]/30"
    >
      <h4 className="text-base font-semibold">{item.title}</h4>
      <p className="mt-1 text-sm text-gray-600">{item.blurb}</p>
      <span className="mt-3 inline-flex items-center text-sm font-medium text-[#0B5EFD]">
        Open <FaExternalLinkAlt className="ml-2" />
      </span>
    </Link>
  );

  return (
    <>
      <Helmet>
        <title>Help Center | Your Choice Associates</title>
        <meta
          name="description"
          content="Find answers fast in the Your Choice Associates Help Center. Browse Medicare and Life Insurance guides, FAQs, and contact support."
        />
      </Helmet>

      <Navbar />

      {/* Hero + Search */}
      <section className="bg-gradient-to-b from-[#f7fbff] to-white">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-14">
          <motion.div {...fadeUp(0)} className="text-center">
            <span className="inline-block rounded-full bg-[#e8f1fb] px-3 py-1 text-xs font-semibold tracking-wider text-[#002B5C]">
              Help Center
            </span>
            <h1 className="mt-4 text-3xl font-extrabold text-[#002B5C] md:text-4xl">
              How can we help?
            </h1>
            <p className="mx-auto mt-3 max-w-2xl text-gray-600">
              Search our knowledge base or browse common topics below.
            </p>
          </motion.div>

          {/* Search */}
          <motion.div {...fadeUp(0.05)} className="mx-auto mt-7 max-w-2xl">
            <label htmlFor="help-search" className="sr-only">
              Search help articles
            </label>
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                id="help-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles, FAQs, topics…"
                className="w-full rounded-2xl border border-gray-300 bg-white py-3 pl-11 pr-4 shadow-sm focus:border-[#0B5EFD] focus:ring-2 focus:ring-[#0B5EFD]/30"
                autoComplete="off"
              />
            </div>
            {q ? (
              <p className="mt-2 text-sm text-gray-600">
                Showing results for <span className="font-medium">“{q}”</span>
              </p>
            ) : null}
          </motion.div>

          {/* Search Results */}
          {q ? (
            <div className="mt-8 grid gap-6 md:grid-cols-12">
              <div className="md:col-span-7 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FaQuestionCircle /> FAQ Matches
                </h3>
                {results.faq.length === 0 ? (
                  <p className="text-sm text-gray-600">No FAQ matches found.</p>
                ) : (
                  <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
                    {results.faq.map((f, i) => (
                      <li key={i} className="p-4">
                        <p className="font-medium">{f.q}</p>
                        <div className="prose prose-sm mt-1 text-gray-600 max-w-none">{f.a}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="md:col-span-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FaClipboardList /> Article Matches
                </h3>
                {results.kb.length === 0 ? (
                  <p className="text-sm text-gray-600">No article matches found.</p>
                ) : (
                  <div className="grid gap-3">
                    {results.kb.map((a) => (
                      <KBCard key={a.id} item={a} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      {/* Categories */}
      {!q && (
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 md:px-8 py-12">
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <CategoryCard
                icon={FaClipboardList}
                title="Medicare Basics"
                blurb="Articles on Parts A–D, Advantage, and Supplements."
                to="/help/medicare-basics"
              />
              <CategoryCard
                icon={FaUserShield}
                title="Life Insurance"
                blurb="Term vs. permanent, riders, and underwriting."
                to="/term-life"
              />
              <CategoryCard
                icon={FaTools}
                title="Using the Website"
                blurb="Finding brokers, submitting forms, and troubleshooting."
                to="/faq"
              />
              <CategoryCard
                icon={FaLifeRing}
                title="Broker & Partner Support"
                blurb="Applying to join, documents, and approval timelines."
                to="/partner-apply"
              />
              <CategoryCard
                icon={FaQuestionCircle}
                title="Frequently Asked Questions"
                blurb="Quick answers to common questions."
                to="/faq"
              />
              <CategoryCard
                icon={FaClipboardList}
                title="Popular Articles"
                blurb="Our most-read guides, hand-picked."
                to="/blog"
              />
            </div>
          </div>
        </section>
      )}

      {/* Popular Articles */}
      <section id="popular" className="bg-[#f8fafc]">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-12">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#002B5C]">Popular articles</h2>
            <Link to="/blog" className="text-sm font-medium text-[#0B5EFD]">
              View all
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {KB_ARTICLES.slice(0, 6).map((a) => (
              <KBCard key={a.id} item={a} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white">
        <div className="mx-auto max-w-3xl px-6 md:px-8 py-14">
          <h2 className="text-xl font-bold text-[#002B5C]">Frequently asked questions</h2>
          <div className="mt-5 divide-y divide-gray-200 overflow-hidden rounded-2xl border border-gray-200 bg-white">
            {FAQ.map((item, idx) => {
              const open = openIdx === idx;
              return (
                <div key={idx}>
                  <button
                    className="flex w-full items-center justify-between px-5 py-4 text-left focus:outline-none"
                    aria-expanded={open}
                    onClick={() => setOpenIdx(open ? null : idx)}
                  >
                    <span className="font-medium">{item.q}</span>
                    <FaChevronDown
                      className={`ml-3 transition ${open ? "rotate-180" : "rotate-0"}`}
                    />
                  </button>
                  <div
                    className={`grid transition-all duration-200 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
                  >
                    <div className="overflow-hidden">
                      <div className="prose prose-sm max-w-none px-5 pb-5 text-gray-700">
                        {item.a}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact / Submit a Request */}
      <section id="contact" className="bg-gradient-to-b from-white to-[#f7fbff]">
        <div className="mx-auto max-w-7xl px-6 md:px-8 py-12">
          <div className="rounded-3xl border border-gray-200 bg-white p-6 md:p-8 shadow-sm">
            <div className="grid items-center gap-8 lg:grid-cols-2">
              <div>
                <h3 className="text-xl font-bold text-[#002B5C]">Still need help?</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Send us a support request and we’ll get back within one business day.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    onClick={() => setModalOpen(true)}
                    className="inline-flex items-center justify-center rounded-xl bg-[#0B5EFD] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#0B5EFD]/40"
                  >
                    <FaLifeRing className="mr-2" /> Submit a Request
                  </button>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0B5EFD]/20"
                  >
                    Or go to Contact
                  </Link>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
                    1
                  </span>
                  Review the relevant guide above.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
                    2
                  </span>
                  Share details (state, prescription list, desired coverage level) in your message.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
                    3
                  </span>
                  We’ll route your request to a licensed broker and follow up.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setModalOpen(false)}
          />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative z-10 w-full max-w-lg rounded-2xl bg-white shadow-xl"
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
              <h4 className="text-base font-semibold">Submit a support request</h4>
              <button
                className="rounded-md p-2 hover:bg-gray-100"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={onSubmit} className="px-5 py-4">
              {err ? (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  {err}
                </div>
              ) : null}
              {sent ? (
                <div className="mb-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                  Thanks! Your request was sent. We'll reply soon.
                </div>
              ) : null}

              <div className="grid gap-3">
                <div className="grid gap-2">
                  <label className="text-sm font-medium" htmlFor="hc-name">
                    Your name
                  </label>
                  <input
                    id="hc-name"
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 focus:border-[#0B5EFD] focus:ring-2 focus:ring-[#0B5EFD]/30"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Jane Doe"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium" htmlFor="hc-email">
                    Email
                  </label>
                  <input
                    id="hc-email"
                    type="email"
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 focus:border-[#0B5EFD] focus:ring-2 focus:ring-[#0B5EFD]/30"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium" htmlFor="hc-phone">
                    Phone (optional)
                  </label>
                  <input
                    id="hc-phone"
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 focus:border-[#0B5EFD] focus:ring-2 focus:ring-[#0B5EFD]/30"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="(555) 555-1234"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium" htmlFor="hc-topic">
                    Topic
                  </label>
                  <select
                    id="hc-topic"
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 focus:border-[#0B5EFD] focus:ring-2 focus:ring-[#0B5EFD]/30"
                    value={form.topic}
                    onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                  >
                    <option>General Question</option>
                    <option>Medicare</option>
                    <option>Life Insurance</option>
                    <option>Broker / Partner Support</option>
                    <option>Website Issue</option>
                    <option>Billing</option>
                  </select>
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium" htmlFor="hc-message">
                    How can we help?
                  </label>
                  <textarea
                    id="hc-message"
                    rows={5}
                    className="rounded-xl border border-gray-300 bg-white px-3 py-2 focus:border-[#0B5EFD] focus:ring-2 focus:ring-[#0B5EFD]/30"
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder="Share details like your state, prescriptions (if Medicare), or preferred coverage."
                  />
                </div>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.consent}
                    onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
                  />
                  I consent to be contacted about my request.
                </label>

                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0B5EFD]/20"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={sending}
                    className="rounded-xl bg-[#0B5EFD] px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#0B5EFD]/40"
                  >
                    {sending ? "Sending…" : "Send request"}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      <Footer />

      {/* Local styles */}
      <style>{`
        .link { color: #0B5EFD; text-decoration: underline; text-underline-offset: 2px; }
      `}</style>
    </>
  );
}
